import _ from 'lodash';
import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Toggle, Icon, TextArea, TextField, Button, Text, Box, Flex, Divider } from '@vibe/core';
import { AppModal, AppModalContent, AppModalFooter } from '@gorilla/ui/src/components/AppModal';
import { Heading } from '@vibe/core';
import { Alert, Email, Person, Hide, Show, TurnInto, Completed, MoveArrowRight, DropdownChevronRight } from '@vibe/icons';
import { type AccountCreate, AccountCreateSchema } from '@gorilla/portal-shared/src/schemas';

type AddAccountModalProps = {
  show: boolean;
  portalUrl: string;
  portalName: string;
  portalGoogleAuthentication: boolean;
  onCreate: (account: AccountCreate & { overwriteExisting: boolean }) => void;
  onClose: () => void;
};

function generatePassword() {
  const length = 20;
  const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  const specialChars = '!@#$%&*()_+[]:;?/=';
  let password = '';
  let hasNumber = false;

  while (password.length < length - 1) {
    const char = charset.charAt(Math.floor(Math.random() * charset.length));
    if (/\d/.test(char)) hasNumber = true;
    password += char;
  }

  if (!hasNumber) {
    const number = '0123456789'.charAt(Math.floor(Math.random() * 10));
    password = password.slice(0, -1) + number;
  }

  const specialChar = specialChars.charAt(Math.floor(Math.random() * specialChars.length));
  const randomIndex = Math.floor(Math.random() * password.length);
  password = password.slice(0, randomIndex) + specialChar + password.slice(randomIndex);

  return password;
}

function generateEmail(type: string, firstname: string, email: string, password: string, portalUrl: string) {
  if (type === 'credentials') {
    return `Hello${firstname !== '' ? ' ' + firstname : ''},

Your account has been created:

Email: ${email}
Password: ${password}

You can now sign in to your account at ${portalUrl} with the credentials provided above.`;
  } else {
    return `You can now sign in with Google at ${portalUrl} using the account associated with ${email} and your password.`;
  }
}

type SendEmailButtonProps = {
  type: string;
  email: string;
  firstname?: string;
  password?: string;
  portalUrl: string;
  portalName: string;
  label: string;
};

function SendEmailButton({ type, email, firstname, password, portalUrl, portalName, label }: SendEmailButtonProps) {
  return (
    <Button
      kind="primary"
      size="medium"
      onClick={() => {
        window.open(
          `mailto:${email}?subject=${
            type === 'credentials'
              ? `Your%20account%20details%20for%20${encodeURIComponent(portalName)}`
              : `Your%20account%20for%20${encodeURIComponent(portalName)}`
          }&body=${encodeURIComponent(generateEmail(type, firstname || '', email, password || '', portalUrl))}`,
        );
      }}
      style={{
        width: '100%',
      }}
    >
      {label}
    </Button>
  );
}

export function AddAccountModal({ show, onCreate, portalUrl, portalName, portalGoogleAuthentication, onClose }: AddAccountModalProps) {
  const { handleSubmit, control, formState, setError, clearErrors, setValue, getValues, watch } = useForm<
    AccountCreate & { overwriteExisting: boolean }
  >({
    resolver: zodResolver(z.intersection(AccountCreateSchema, z.object({ overwriteExisting: z.boolean() }))),
    defaultValues: {
      enabled: true,
      note: '',
      overwriteExisting: false,
    },
  });

  const doSubmit = handleSubmit(async (data) => {
    try {
      const rawData = getValues();
      await onCreate({ ...data, overwriteExisting: rawData.overwriteExisting, email: data.email.toLowerCase() });
      clearErrors('root.serverError');
    } catch (err) {
      setError('root.serverError', { message: _.get(err, 'message') || 'An error occurred' });
    }
  });

  const type = watch('type');
  const serverError = formState?.errors.root?.serverError;
  const modalStep = !type ? 'select' : formState.isSubmitted && !serverError && !formState.isSubmitting ? 'done' : 'form';
  const [hidePassword, setHidePassword] = useState<true | false>(true);

  let modalHeadline = 'Create login method';
  let modalHeadlineStep = 'Choose type';
  let content = null;

  if (modalStep === 'select') {
    content = (
      <Box
        rounded="small"
        border={true}
        borderColor="uiBorderColor"
        style={{
          margin: '24px 0 12px 0',
          backgroundColor: 'rgba(103, 104, 121, 0.025)',
        }}
      >
        <Flex
          direction="column"
          justify="center"
          align="center"
          gap={8}
          style={{
            margin: '0 auto',
            width: '100%',
          }}
        >
          <Flex direction="column" align="stretch" gap={0} style={{ width: '100%' }}>
            <div
              style={{
                padding: '0 28px 0 16px',
              }}
              className="boardsource__item"
              onClick={() => {
                setValue('type', 'credentials');
              }}
            >
              <Flex
                direction="row"
                justify="start"
                align="center"
                gap={32}
                style={{
                  pointerEvents: 'none',
                  marginLeft: '8px',
                  height: '80px',
                }}
              >
                <Flex
                  direction="column"
                  justify="start"
                  align="start"
                  gap={4}
                  style={{
                    color: '#323338',
                  }}
                >
                  <Heading type="h3" weight="bold" align="start" color="inherit">
                    Credentials
                  </Heading>
                  <Text type="text3" align="start" color="secondary" element="p">
                    <span className="boardsource__description boardsource__description--show">Client login with email and password</span>
                  </Text>
                </Flex>
                <Box
                  style={{
                    marginLeft: 'auto',
                    overflow: 'visible',
                    position: 'relative',
                  }}
                >
                  <Flex gap={2}>
                    <div
                      style={{
                        width: '28px',
                        height: '24px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        borderRadius: '4px',
                        lineHeight: '0',
                        color: '#323338',
                      }}
                      className="boardsource__icon"
                    >
                      <Icon icon={MoveArrowRight} size={18} />
                    </div>
                  </Flex>
                </Box>
              </Flex>
            </div>
            <Divider withoutMargin />
            <div
              style={{
                padding: '0 28px 0 16px',
              }}
              className="boardsource__item"
              onClick={() => {
                setValue('type', 'google');
              }}
            >
              <Flex
                direction="row"
                justify="start"
                align="center"
                gap={32}
                style={{
                  pointerEvents: 'none',
                  marginLeft: '8px',
                  height: '80px',
                }}
              >
                <Flex
                  direction="column"
                  justify="start"
                  align="start"
                  gap={4}
                  style={{
                    color: '#323338',
                  }}
                >
                  <Heading type="h3" weight="bold" align="start" color="inherit">
                    Google
                  </Heading>
                  <Text type="text3" align="start" color="secondary" element="p">
                    <span className="boardsource__description boardsource__description--show">Client login with Google account</span>
                  </Text>
                </Flex>
                <Box
                  style={{
                    overflow: 'visible',
                    position: 'relative',
                    marginLeft: 'auto',
                  }}
                >
                  <Flex gap={2}>
                    <div
                      style={{
                        width: '28px',
                        height: '24px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        borderRadius: '4px',
                        lineHeight: '0',
                        color: '#323338',
                      }}
                      className="boardsource__icon"
                    >
                      <Icon icon={MoveArrowRight} size={18} />
                    </div>
                  </Flex>
                </Box>
              </Flex>
            </div>
          </Flex>
        </Flex>
      </Box>
    );
  } else if (modalStep === 'form') {
    modalHeadlineStep = `${_.upperFirst(type || '')}`;
    content = (
      <Box marginTop={'medium' as any}>
        {type === 'google' && !portalGoogleAuthentication ? (
          <Box
            marginBottom="medium"
            padding="medium"
            rounded="medium"
            style={{
              backgroundColor: '#ffcb00',
            }}
          >
            <Flex gap={10} align="center">
              <div>
                <Icon icon={Alert} size={20} />
              </div>
              <Text type="text3" ellipsis={false}>
                Google authentication is disabled in the portal settings. Make sure to enable it before sharing any Google login methods.
              </Text>
            </Flex>
          </Box>
        ) : null}

        <form onSubmit={doSubmit}>
          <Controller
            name="enabled"
            control={control}
            render={({ field }) => {
              return (
                <Flex>
                  <Flex gap={8}>Enable login method</Flex>
                  <Box style={{ marginLeft: 'auto' }}>
                    <Box
                      style={{
                        marginRight: '-8px',
                        marginLeft: '-8px',
                      }}
                    >
                      <Toggle
                        offOverrideText=""
                        onOverrideText=""
                        isSelected={!!field.value}
                        onChange={(value: any) => {
                          field.onChange(value);
                        }}
                        size="small"
                        disabled={formState.isSubmitting}
                      />
                    </Box>
                  </Box>
                </Flex>
              );
            }}
          />

          <Controller
            name="overwriteExisting"
            control={control}
            render={({ field }) => {
              return (
                <Flex style={{ marginTop: '8px' }}>
                  <Flex gap={8}>Overwrite existing login method</Flex>
                  <Box style={{ marginLeft: 'auto' }}>
                    <Box
                      style={{
                        marginRight: '-8px',
                        marginLeft: '-8px',
                      }}
                    >
                      <Toggle
                        offOverrideText=""
                        onOverrideText=""
                        isSelected={!!field.value}
                        onChange={(value: any) => {
                          field.onChange(value);
                        }}
                        size="small"
                        disabled={formState.isSubmitting}
                      />
                    </Box>
                  </Box>
                </Flex>
              );
            }}
          />

          {type === 'credentials' ? (
            <Flex gap={16} style={{ marginTop: '24px' }}>
              <Controller
                name="firstname"
                control={control}
                rules={{ required: 'First name is required' }}
                render={({ field }) => {
                  return (
                    <TextField
                      id="firstname"
                      placeholder="First name"
                      value={field.value}
                      onChange={field.onChange}
                      size="medium"
                      autoComplete="off"
                      autoFocus
                      disabled={formState.isSubmitting}
                    />
                  );
                }}
              />
              <Controller
                name="lastname"
                control={control}
                rules={{ required: 'Last name is required' }}
                render={({ field }) => {
                  return (
                    <TextField
                      id="lastname"
                      placeholder="Last name"
                      value={field.value}
                      onChange={field.onChange}
                      size="medium"
                      autoComplete="off"
                      disabled={formState.isSubmitting}
                    />
                  );
                }}
              />
            </Flex>
          ) : null}

          {type === 'credentials' || type === 'google' ? (
            <Controller
              name="email"
              control={control}
              rules={{ required: 'Email is required' }}
              render={({ field }) => {
                return (
                  <Flex style={{ marginTop: '16px' }}>
                    <TextField
                      id="email"
                      placeholder={type === 'google' ? 'e.g. john@gmail.com' : 'Email'}
                      value={field.value}
                      onChange={field.onChange}
                      size="medium"
                      icon={Email}
                      autoComplete="off"
                      validation={{
                        text: type === 'google' ? 'This must be the exact email address of the Google account.' : '',
                      }}
                      disabled={formState.isSubmitting}
                      autoFocus={type === 'google'}
                    />
                  </Flex>
                );
              }}
            />
          ) : null}

          {type === 'credentials' ? (
            <Controller
              name="password"
              control={control}
              rules={{ required: 'Password is required' }}
              render={({ field }) => {
                return (
                  <Flex gap={4} direction="column" justify="start" style={{ marginTop: '16px' }}>
                    <TextField
                      id="password"
                      type={hidePassword ? 'password' : 'text'}
                      value={field.value}
                      onChange={field.onChange}
                      size="medium"
                      placeholder="Password"
                      icon={hidePassword ? Show : Hide}
                      onIconClick={() => {
                        setHidePassword((prevHidePassword) => !prevHidePassword);
                      }}
                      autoComplete="off"
                      disabled={formState.isSubmitting}
                    />
                    <Box
                      style={{
                        width: '100%',
                        WebkitFontSmoothing: 'var(--font-smoothing-webkit)',
                        MozOsxFontSmoothing: 'var(--font-smoothing-moz)',
                      }}
                    >
                      <Text type="text2" weight="normal" color="secondary">
                        Minimum 8 characters, one number and symbol.
                        <a
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            setHidePassword(false);
                            const generatedPassword = generatePassword();
                            field.onChange(generatedPassword);
                          }}
                          style={{
                            pointerEvents: formState.isSubmitting ? 'none' : 'auto',
                            marginLeft: '10px',
                            color: formState.isSubmitting ? '#676879' : '#0073ea',
                          }}
                        >
                          <div style={{ display: 'inline-block', marginRight: '4px', transform: 'translate3d(0,3px,0)' }}>
                            <Icon icon={TurnInto} />
                          </div>
                          Generate
                        </a>
                      </Text>
                    </Box>
                  </Flex>
                );
              }}
            />
          ) : null}

          <Controller
            name="note"
            control={control}
            render={({ field }) => {
              return (
                <Flex style={{ marginTop: '24px' }}>
                  <TextArea
                    id="note"
                    label="Comment"
                    //helpText="Add a comment for internal use or reference."
                    value={field.value}
                    onChange={field.onChange}
                    autoComplete="off"
                    disabled={formState.isSubmitting}
                  />
                </Flex>
              );
            }}
          />
        </form>
      </Box>
    );
  } else {
    modalHeadline = 'Login method created';
    content = (
      <Box marginTop="medium">
        <Text type="text2" align="start" color="primary" element="p">
          The login method has been successfully created. You can now send the user an email with the details from your email software.
        </Text>
        {type === 'google' && !portalGoogleAuthentication ? (
          <Box
            marginTop="medium"
            padding="medium"
            rounded="medium"
            style={{
              backgroundColor: '#ffcb00',
            }}
          >
            <Flex gap={10} align="center">
              <div>
                <Icon icon={Alert} size={20} />
              </div>
              <Text type="text3" ellipsis={false}>
                Google authentication is disabled in the portal settings. Make sure to enable it before sharing any Google login methods.
              </Text>
            </Flex>
          </Box>
        ) : null}
        <Flex gap={16} style={{ marginTop: '24px', marginBottom: '10px' }}>
          <Box style={{ flex: 1 }}>
            {type === 'credentials' ? (
              <SendEmailButton
                type={type}
                email={getValues('email')}
                firstname={getValues('firstname')!}
                password={getValues('password')!}
                portalUrl={portalUrl}
                portalName={portalName}
                label="Send credentials to user by email"
              />
            ) : (
              <SendEmailButton
                type={type}
                email={getValues('email')}
                portalUrl={portalUrl}
                portalName={portalName}
                label="Send onboarding email to user"
              />
            )}
          </Box>
          <Box style={{ width: 'auto' }}>
            <Button
              kind="secondary"
              onClick={() => {
                onClose();
              }}
              style={{
                width: '100%',
              }}
            >
              Close
            </Button>
          </Box>
          {/* 
          <Box style={{ width: '50%' }}>
            <Button
              onClick={() => {
                // Implement reset to step = 'select'
                reset();
              }}
              style={{
                width: '100%',
              }}
            >
              Create new account
            </Button>
          </Box>
          */}
        </Flex>
      </Box>
    );
  }

  return (
    <AppModal
      show={show}
      onClose={() => {
        onClose();
      }}
      width={400}
      alertDialog={false}
      data-testid="add-account-modal"
    >
      <AppModalContent>
        <Box>
          <Flex gap={12} style={{ marginBottom: modalStep === 'select' || modalStep === 'form' ? '32px' : '24px' }}>
            {modalStep === 'select' || modalStep === 'form' ? (
              <Icon icon={Person} size={22} style={{ transform: 'translate3d(0,-1px,0)' }} />
            ) : (
              <Icon icon={Completed} size={22} style={{ transform: 'translate3d(0,-1px,0)' }} />
            )}
            <Flex gap={4}>
              <Heading type="h3" weight="normal" align="start" maxLines={1} ellipsis={false}>
                {modalHeadline}
              </Heading>
              {modalStep === 'select' || modalStep === 'form' ? (
                <>
                  <Icon icon={DropdownChevronRight} size={22} style={{ transform: 'translate3d(0,0,0)' }} />
                  <Heading type="h3" weight="bold" align="start" maxLines={1} ellipsis={false}>
                    {modalHeadlineStep}
                  </Heading>
                </>
              ) : null}
            </Flex>
          </Flex>
          {content}
        </Box>
      </AppModalContent>
      {modalStep === 'form' ? (
        <AppModalFooter>
          <Flex gap={12} justify={'end' as any}>
            <Box marginEnd={'auto' as any}>
              {serverError ? (
                <Flex gap={6} style={{ color: '#d83a52' }}>
                  <Icon icon={Alert} size={20} />
                  <div style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', fontSize: '14px' }}>
                    {serverError.message}
                  </div>
                </Flex>
              ) : (
                <Flex gap={6} style={{ color: '#afb0bd' }}>
                  <Icon icon={Email} size={20} />
                  <div style={{ fontSize: '14px' }}>Create new login method</div>
                </Flex>
              )}
            </Box>
            <Button
              kind={!formState.isSubmitting ? 'primary' : 'tertiary'}
              size="medium"
              disabled={!formState.isValid || formState.isSubmitting}
              loading={formState.isSubmitting}
              onClick={() => {
                doSubmit();
              }}
            >
              Create
            </Button>
          </Flex>
        </AppModalFooter>
      ) : (
        <></>
      )}
    </AppModal>
  );
}
